import React, { useState, useEffect } from 'react';

const CRMDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const crmAuth = localStorage.getItem('crmAuth') || btoa('admin:kioo2025!');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, donationsRes, visitorsRes, prayersRes, contactsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/crm/stats`, { headers: { 'Authorization': `Basic ${crmAuth}` } }),
        fetch(`${BACKEND_URL}/api/donations?limit=5`, { headers: { 'Authorization': `Basic ${crmAuth}` } }),
        fetch(`${BACKEND_URL}/api/crm/visitors?limit=5`, { headers: { 'Authorization': `Basic ${crmAuth}` } }),
        fetch(`${BACKEND_URL}/api/crm/prayer-requests?limit=5`, { headers: { 'Authorization': `Basic ${crmAuth}` } }).catch(() => ({ ok: false })),
        fetch(`${BACKEND_URL}/api/crm/contacts?limit=5`, { headers: { 'Authorization': `Basic ${crmAuth}` } }).catch(() => ({ ok: false }))
      ]);

      const statsData = statsRes.ok ? await statsRes.json() : {};
      const donationsData = donationsRes.ok ? await donationsRes.json() : [];
      const visitorsData = visitorsRes.ok ? await visitorsRes.json() : { visitors: [] };
      const prayersData = prayersRes.ok ? await prayersRes.json() : { requests: [] };
      const contactsData = contactsRes.ok ? await contactsRes.json() : { contacts: [] };

      // Calculate additional stats
      const donations = Array.isArray(donationsData) ? donationsData : [];
      const thisMonth = new Date().getMonth();
      const thisYear = new Date().getFullYear();
      
      const monthlyDonations = donations.filter(d => {
        const date = new Date(d.date_iso || d.created_at);
        return date.getMonth() === thisMonth && date.getFullYear() === thisYear;
      });
      
      const totalThisMonth = monthlyDonations.reduce((sum, d) => sum + (d.amount || 0), 0);
      const totalAllTime = donations.reduce((sum, d) => sum + (d.amount || 0), 0);

      setStats({
        ...statsData,
        donations: {
          thisMonth: totalThisMonth,
          allTime: totalAllTime,
          count: donations.length,
          monthlyCount: monthlyDonations.length
        },
        visitors: visitorsData.visitors?.length || 0,
        prayers: prayersData.requests?.length || 0,
        contacts: contactsData.contacts?.length || 0
      });

      // Build recent activity
      const activities = [];
      
      donations.slice(0, 3).forEach(d => {
        activities.push({
          type: 'donation',
          icon: '💰',
          text: `${d.donor_name || 'Anonymous'} donated ${d.amount_currency || 'USD'} ${d.amount?.toLocaleString() || 0}`,
          date: d.date_iso || d.created_at,
          color: 'green'
        });
      });

      (visitorsData.visitors || []).slice(0, 3).forEach(v => {
        activities.push({
          type: 'visitor',
          icon: '👤',
          text: `${v.name} visited from ${v.country || 'Unknown'}`,
          date: v.visit_date || v.created_at,
          color: 'blue'
        });
      });

      (prayersData.requests || []).slice(0, 3).forEach(p => {
        activities.push({
          type: 'prayer',
          icon: '🙏',
          text: `New prayer request: "${(p.request || '').substring(0, 50)}..."`,
          date: p.created_at,
          color: 'purple'
        });
      });

      // Sort by date
      activities.sort((a, b) => new Date(b.date) - new Date(a.date));
      setRecentActivity(activities.slice(0, 8));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⏳</div>
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">📊 CRM Dashboard</h1>
        <p className="text-gray-600 mt-1">Overview of your radio station's key metrics</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Donations This Month */}
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Donations This Month</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(stats?.donations?.thisMonth)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {stats?.donations?.monthlyCount || 0} donations
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">💰</span>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-green-600">↑ Active</span>
            <span className="text-gray-400 ml-2">fundraising</span>
          </div>
        </div>

        {/* Total Contacts */}
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Total Contacts</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.contacts || stats?.total_contacts || 0}
              </p>
              <p className="text-xs text-gray-400 mt-1">Listeners & supporters</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">👥</span>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-blue-600">📧</span>
            <span className="text-gray-400 ml-2">Connected community</span>
          </div>
        </div>

        {/* Prayer Requests */}
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Prayer Requests</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.prayers || stats?.pending_prayers || 0}
              </p>
              <p className="text-xs text-gray-400 mt-1">Needs follow-up</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">🙏</span>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-purple-600">Active</span>
            <span className="text-gray-400 ml-2">prayer ministry</span>
          </div>
        </div>

        {/* Station Visitors */}
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Station Visitors</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.visitors || 0}
              </p>
              <p className="text-xs text-gray-400 mt-1">Total recorded</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">🏠</span>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-orange-600">Welcome</span>
            <span className="text-gray-400 ml-2">guests</span>
          </div>
        </div>
      </div>

      {/* Charts and Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Monthly Goals */}
        <div className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2">
          <h3 className="font-semibold text-gray-900 mb-4">📈 Monthly Goals Progress</h3>
          
          {/* Fundraising Goal */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Fundraising Goal</span>
              <span className="font-medium">{formatCurrency(stats?.donations?.thisMonth)} / $5,000</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(((stats?.donations?.thisMonth || 0) / 5000) * 100, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* New Contacts Goal */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">New Contacts Goal</span>
              <span className="font-medium">{stats?.new_contacts_month || 0} / 50</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(((stats?.new_contacts_month || 0) / 50) * 100, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Prayers Answered */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Prayers Followed Up</span>
              <span className="font-medium">{stats?.prayers_answered || 0} / {stats?.prayers || 10}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-purple-500 to-purple-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(((stats?.prayers_answered || 0) / (stats?.prayers || 10)) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-4">⚡ Quick Stats</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">All-Time Donations</span>
              <span className="font-bold text-green-600">{formatCurrency(stats?.donations?.allTime)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Total Donations</span>
              <span className="font-bold text-gray-900">{stats?.donations?.count || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Pending Invoices</span>
              <span className="font-bold text-orange-600">{stats?.pending_invoices || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Active Programs</span>
              <span className="font-bold text-blue-600">{stats?.active_programs || 12}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-gray-900">🕐 Recent Activity</h3>
          <button 
            onClick={fetchDashboardData}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            ↻ Refresh
          </button>
        </div>
        
        {recentActivity.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No recent activity</p>
        ) : (
          <div className="space-y-3">
            {recentActivity.map((activity, index) => (
              <div 
                key={index}
                className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <span className="text-2xl mr-3">{activity.icon}</span>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{activity.text}</p>
                  <p className="text-xs text-gray-500">{formatDate(activity.date)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CRMDashboard;
